export type DialogueFitFn = (text: string) => boolean;

const TINY_PAGE_CHARS = 42;

function isDigit(ch: string | undefined): boolean {
  return !!ch && ch >= '0' && ch <= '9';
}

/** Pecah di titik/tanya/seru + spasi, bukan di 3.5 atau singkatan tanpa huruf kapital. */
export function splitSentences(text: string): string[] {
  const source = text.replace(/\s+/g, ' ').trim();
  if (!source) return [' '];

  const parts: string[] = [];
  let start = 0;

  for (let i = 0; i < source.length; i++) {
    const ch = source[i];
    if (ch !== '.' && ch !== '!' && ch !== '?') continue;

    let end = i;
    while (
      end + 1 < source.length
      && (source[end + 1] === '.' || source[end + 1] === '!' || source[end + 1] === '?')
    ) {
      end++;
    }

    const prev = source[i - 1];
    const next = source[end + 1];
    if (isDigit(prev) && isDigit(next)) {
      i = end;
      continue;
    }

    const after = source.slice(end + 1);
    const spaceMatch = after.match(/^\s*/);
    const spaceLen = spaceMatch ? spaceMatch[0].length : 0;
    const rest = after.slice(spaceLen);
    const isEnd = rest.length === 0;
    const nextSentence = /^[A-ZÀ-ÖØ-Ý]/.test(rest) || /^[“"'][A-ZÀ-ÖØ-Ý]/.test(rest);

    if (isEnd || (spaceLen > 0 && nextSentence)) {
      const chunk = source.slice(start, end + 1).trim();
      if (chunk) parts.push(chunk);
      start = end + 1 + spaceLen;
      i = start - 1;
    } else {
      i = end;
    }
  }

  const tail = source.slice(start).trim();
  if (tail) parts.push(tail);
  return parts.length ? parts : [source];
}

function splitClauses(text: string): string[] {
  const parts = text.split(/\s*[,;—–]\s*/).map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts : [text];
}

function packWhileFit(pieces: string[], joiner: string, fits: DialogueFitFn): string[] {
  const pages: string[] = [];
  let current = '';

  for (const piece of pieces) {
    if (!piece) continue;
    const next = current ? `${current}${joiner}${piece}` : piece;
    if (!current) {
      if (fits(piece)) {
        current = piece;
      } else {
        pages.push(...splitOverflow(piece, fits));
        current = '';
      }
      continue;
    }
    if (fits(next)) {
      current = next;
    } else {
      pages.push(current);
      if (fits(piece)) {
        current = piece;
      } else {
        const overflow = splitOverflow(piece, fits);
        pages.push(...overflow.slice(0, -1));
        current = overflow[overflow.length - 1] ?? '';
      }
    }
  }

  if (current) pages.push(current);
  return pages.length ? pages : [' '];
}

function splitOverflow(text: string, fits: DialogueFitFn): string[] {
  if (fits(text)) return [text];

  const clauses = splitClauses(text);
  if (clauses.length > 1) {
    const packed = packWhileFit(clauses, ', ', fits);
    if (packed.length > 1 || (packed[0] && packed[0] !== text)) {
      return packed.flatMap((p) => (fits(p) ? [p] : splitByWords(p, fits)));
    }
  }

  return splitByWords(text, fits);
}

function splitByWords(text: string, fits: DialogueFitFn): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return [text || ' '];

  const pages: string[] = [];
  let current = '';
  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (!current || fits(next)) {
      current = next;
    } else {
      if (current) pages.push(current);
      current = word;
    }
  }
  if (current) pages.push(current);
  return pages.length ? pages : [text];
}

function mergeTinyPages(pages: string[], fits: DialogueFitFn): string[] {
  if (pages.length < 2) return pages;
  const merged: string[] = [];
  for (const page of pages) {
    const prev = merged[merged.length - 1];
    if (
      prev
      && page.length <= TINY_PAGE_CHARS
      && fits(`${prev} ${page}`)
    ) {
      merged[merged.length - 1] = `${prev} ${page}`;
    } else {
      merged.push(page);
    }
  }
  return merged;
}

function wordsOf(text: string): string[] {
  return text.split(/\s+/).filter(Boolean);
}

function contentWords(text: string): string[] {
  return wordsOf(text)
    .map((word) => word.replace(/^[“"'(\[]+|[)”"',.;:!?—–]+$/g, ''))
    .filter(Boolean);
}

function pagesPreserveWords(source: string, pages: string[]): boolean {
  const srcWords = contentWords(source);
  const pageWords = contentWords(pages.join(' '));
  if (srcWords.length !== pageWords.length) return false;
  return srcWords.every((word, i) => word === pageWords[i]);
}

/** Halaman dialog: utamakan kalimat utuh, lalu koma, baru kata. Tidak pernah array kosong. */
export function paginateDialogueText(text: string, fits: DialogueFitFn): string[] {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (!normalized) return [' '];
  if (fits(normalized)) return [normalized];

  const sentences = splitSentences(normalized);
  let packed = mergeTinyPages(packWhileFit(sentences, ' ', fits), fits);
  if (!packed.length || !pagesPreserveWords(normalized, packed)) {
    packed = mergeTinyPages(splitByWords(normalized, fits), fits);
  }
  return packed.length ? packed : [' '];
}
